import React, { useState, useEffect } from 'react';
import { ArrowRightLeft, X, Loader2, AlertTriangle, Check, DollarSign } from 'lucide-react';
import apiService from '../services/api';
import { useToast } from '../context/ToastContext';

interface Props {
  open: boolean;
  onClose: () => void;
  workspaceId: number;
  workspaceName: string;
  isLifetime?: boolean;
  hasActiveSubscription?: boolean;
  onTransferInitiated: () => void;
}

interface CreditEstimate {
  has_subscription: boolean;
  credit_amount: number;
  currency: string;
}

type Step = 'email' | 'confirm' | 'success';

const TransferWorkspaceModal: React.FC<Props> = ({
  open,
  onClose,
  workspaceId,
  workspaceName,
  isLifetime,
  hasActiveSubscription,
  onTransferInitiated,
}) => {
  const { showError } = useToast();
  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
const [transferResult, setTransferResult] = useState<{ expires_at: string } | null>(null);
  const [creditEstimate, setCreditEstimate] = useState<CreditEstimate | null>(null);
  const [loadingEstimate, setLoadingEstimate] = useState(false);
  const [creditWarning, setCreditWarning] = useState<{ show_warning: boolean; available_count: number } | null>(null);

  // Focus/hover states
  const [emailFocused, setEmailFocused] = useState(false);
  const [cancelHovered, setCancelHovered] = useState(false);
  const [nextHovered, setNextHovered] = useState(false);
  const [confirmHovered, setConfirmHovered] = useState(false);
  const [backHovered, setBackHovered] = useState(false);
  const [doneHovered, setDoneHovered] = useState(false);

  // Fetch credit estimate when modal opens and there's an active subscription
  useEffect(() => {
    if (open && hasActiveSubscription && !isLifetime) {
      setLoadingEstimate(true);
      apiService.get(`/workspaces/${workspaceId}/transfer-estimate`)
        .then((response: any) => {
          setCreditEstimate(response.data);
        })
        .catch((err: any) => {
          console.error('Failed to fetch transfer estimate:', err);
          setCreditEstimate(null);
        })
.finally(() => {
          setLoadingEstimate(false);
        });
    }
  }, [open, workspaceId, hasActiveSubscription, isLifetime]);

  // Decision (c) soft warning: last lifetime workspace + held review credits = credits
  // can't be redeemed after transfer. Fail-safe: any error just hides the warning.
  useEffect(() => {
    if (open && isLifetime) {
      apiService.get(`/workspaces/${workspaceId}/transfer-credit-warning`)
        .then((response: any) => setCreditWarning(response.data))
        .catch(() => setCreditWarning(null));
    }
  }, [open, workspaceId, isLifetime]);

  const resetModal = () => {
    setStep('email');
    setEmail('');
    setSubmitting(false);
setTransferResult(null);
    setCreditEstimate(null);
    setCreditWarning(null);
  };

  const handleClose = () => {
    resetModal();
    onClose();
  };

  const handleInitiateTransfer = async () => {
    setSubmitting(true);
    try {
      const response = await apiService.post(`/workspaces/${workspaceId}/transfer`, {
        target_email: email.toLowerCase().trim(),
      });
      setTransferResult(response.data);
      setStep('success');
      onTransferInitiated();
    } catch (err: any) {
      showError(err.response?.data?.error || err.message || 'Failed to initiate transfer');
    } finally {
      setSubmitting(false);
    }
  };

  const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

  if (!open) return null;

  // Shared styles
  const overlayStyle: React.CSSProperties = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(15, 23, 42, 0.4)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
  };

  const modalStyle: React.CSSProperties = {
    backgroundColor: 'var(--v2-surface)',
    borderRadius: 'var(--radius-lg)',
    boxShadow: 'var(--shadow-lg)',
    border: '1px solid var(--v2-border)',
    maxWidth: '520px',
    width: '90%',
    maxHeight: '90vh',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    fontFamily: 'var(--font-sans)',
  };

  const headerStyle: React.CSSProperties = {
    padding: '20px 24px',
    borderBottom: '1px solid var(--v2-border)',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  };

  const contentStyle: React.CSSProperties = {
    padding: '24px',
    overflowY: 'auto',
    flex: 1,
  };

  const footerStyle: React.CSSProperties = {
    padding: '16px 24px',
    borderTop: '1px solid var(--v2-border)',
    backgroundColor: 'var(--v2-surface-alt)',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
  };

  const getInputStyle = (focused: boolean): React.CSSProperties => ({
    width: '100%',
    padding: '12px 14px',
    backgroundColor: 'var(--v2-surface)',
    border: `1px solid ${focused ? 'var(--v2-primary)' : 'var(--v2-border)'}`,
    borderRadius: 'var(--radius-md)',
    fontSize: '0.9375rem',
    color: 'var(--v2-text)',
    outline: 'none',
    transition: 'all 0.15s ease',
    boxSizing: 'border-box' as const,
    boxShadow: focused ? '0 0 0 3px var(--v2-primary-muted)' : 'none',
  });

  const getSecondaryBtnStyle = (hovered: boolean): React.CSSProperties => ({
    padding: '10px 20px',
    backgroundColor: hovered ? 'var(--v2-surface-hover)' : 'var(--v2-surface)',
    color: hovered ? 'var(--v2-text)' : 'var(--v2-text-tertiary)',
    border: '1px solid var(--v2-border)',
    borderRadius: 'var(--radius-md)',
    fontSize: '0.875rem',
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.15s',
  });

  const getPrimaryBtnStyle = (hovered: boolean, disabled: boolean): React.CSSProperties => ({
    padding: '10px 20px',
    backgroundColor: disabled ? 'var(--v2-border)' : hovered ? 'var(--v2-primary-hover)' : 'var(--v2-primary)',
    color: disabled ? 'var(--v2-text-muted)' : '#ffffff',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    fontSize: '0.875rem',
    fontWeight: 500,
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: 'background-color 0.15s',
    minWidth: '120px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
  });

  const infoBoxStyle: React.CSSProperties = {
    padding: '16px',
    backgroundColor: 'var(--v2-surface-alt)',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--v2-border)',
    marginBottom: '20px',
  };

  const warningBoxStyle: React.CSSProperties = {
    padding: '14px 16px',
    backgroundColor: 'var(--v2-warning-bg)',
    borderRadius: 'var(--radius-md)',
    border: '1px solid #fcd34d',
    display: 'flex',
    gap: '12px',
    marginBottom: '20px',
  };

  const creditBoxStyle: React.CSSProperties = {
    padding: '14px 16px',
    backgroundColor: 'var(--v2-success-bg)',
    borderRadius: 'var(--radius-md)',
    border: '1px solid #86efac',
    display: 'flex',
    gap: '12px',
    marginBottom: '20px',
  };

  const successBoxStyle: React.CSSProperties = {
    padding: '16px',
    backgroundColor: 'var(--v2-success-bg)',
    borderRadius: 'var(--radius-md)',
    border: '1px solid #86efac',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    marginBottom: '20px',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: '0.6875rem',
    fontWeight: 600,
    color: 'var(--v2-text-muted)',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    marginBottom: '8px',
  };

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  return (
    <div
      style={overlayStyle}
      onClick={(e) => {
        if (e.target === e.currentTarget) handleClose();
      }}
    >
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={headerStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div
              style={{
                width: '40px',
                height: '40px',
                borderRadius: 'var(--radius-md)',
                backgroundColor: step === 'success' ? 'var(--v2-success-bg)' : 'var(--v2-warning-bg)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {step === 'success' ? (
                <Check size={20} strokeWidth={2} color="var(--v2-success)" />
              ) : (
                <ArrowRightLeft size={20} strokeWidth={1.5} color="#92400e" />
              )}
            </div>
            <div>
              <h2
                style={{
                  fontSize: '1.125rem',
                  fontWeight: 600,
                  color: 'var(--v2-text)',
                  margin: 0,
                  letterSpacing: '-0.025em',
                }}
              >
                {step === 'success' ? 'Transfer Initiated' : 'Transfer Workspace'}
              </h2>
              <p style={{ fontSize: '0.875rem', color: 'var(--v2-text-tertiary)', margin: '2px 0 0 0' }}>
                {workspaceName}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            style={{
              background: 'none',
              border: 'none',
              padding: '8px',
              cursor: 'pointer',
              borderRadius: 'var(--radius-sm)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--v2-text-muted)',
              transition: 'all 0.15s',
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--v2-surface-hover)';
              e.currentTarget.style.color = 'var(--v2-text)';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = 'var(--v2-text-muted)';
            }}
          >
            <X size={20} strokeWidth={1.5} />
          </button>
        </div>

        {/* Content */}
        <div style={contentStyle}>
          {/* Step 1: Email Input */}
          {step === 'email' && (
            <>
              <p style={{ fontSize: '0.875rem', color: 'var(--v2-text-secondary)', margin: '0 0 20px 0', lineHeight: 1.6 }}>
                Transfer ownership of this workspace to another DashboardFox user. They must already have an account.
              </p>

              <div style={{ marginBottom: '20px' }}>
                <label style={labelStyle}>Recipient Email</label>
                <input
                  type="email"
                  placeholder="recipient@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onFocus={() => setEmailFocused(true)}
                  onBlur={() => setEmailFocused(false)}
                  style={getInputStyle(emailFocused)}
                  autoFocus
                />
                <p style={{ fontSize: '0.8125rem', color: 'var(--v2-text-tertiary)', margin: '8px 0 0 0' }}>
                  The recipient will receive an email with a link to accept the transfer.
                </p>
              </div>
            </>
          )}

          {/* Step 2: Confirmation */}
          {step === 'confirm' && (
            <>
              <div style={infoBoxStyle}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ fontSize: '0.8125rem', color: 'var(--v2-text-tertiary)' }}>Workspace</span>
                  <span style={{ fontSize: '0.8125rem', fontWeight: 500, color: 'var(--v2-text)' }}>{workspaceName}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: '0.8125rem', color: 'var(--v2-text-tertiary)' }}>Transfer to</span>
                  <span style={{ fontSize: '0.8125rem', fontWeight: 500, color: 'var(--v2-text)', fontFamily: 'var(--font-mono)' }}>
                    {email}
                  </span>
                </div>
              </div>

{/* Credit estimate for active subscriptions */}
              {!isLifetime && creditEstimate?.has_subscription && (
                <div style={creditEstimate.credit_amount > 0 ? creditBoxStyle : infoBoxStyle}>
                  <DollarSign size={20} strokeWidth={1.5} color={creditEstimate.credit_amount > 0 ? "#166534" : "var(--v2-text-tertiary)"} style={{ flexShrink: 0, marginTop: '2px' }} />
                  <div>
                    {creditEstimate.credit_amount > 0 ? (
                      <React.Fragment>
                        <p style={{ fontSize: '0.875rem', fontWeight: 600, color: '#166534', margin: '0 0 4px 0' }}>
                          Account Credit: {formatCurrency(creditEstimate.credit_amount, creditEstimate.currency)}
                        </p>
                        <p style={{ fontSize: '0.8125rem', color: '#166534', margin: 0 }}>
                          Your remaining subscription value will be credited to your account for future use. Credits are non-refundable.
                        </p>
                      </React.Fragment>
                    ) : (
                      <p style={{ fontSize: '0.8125rem', color: 'var(--v2-text-secondary)', margin: 0 }}>
                        Your active subscription will be cancelled immediately upon transfer. No prorated credit is available at this time.
                      </p>
                    )}
                  </div>
                </div>
              )}

              {loadingEstimate && (
                <div style={{ ...infoBoxStyle, display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <Loader2 size={16} strokeWidth={2} style={{ animation: 'spin 1s linear infinite' }} />
                  <span style={{ fontSize: '0.8125rem', color: 'var(--v2-text-secondary)' }}>Calculating subscription credit...</span>
                </div>
              )}

{isLifetime && creditWarning?.show_warning && (
                <div style={infoBoxStyle}>
                  <p style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--v2-text)', margin: '0 0 4px 0' }}>
                    You have {creditWarning.available_count} unredeemed review credit{creditWarning.available_count === 1 ? '' : 's'}
                  </p>
                  <p style={{ fontSize: '0.8125rem', color: 'var(--v2-text-secondary)', margin: 0, lineHeight: 1.6 }}>
                    Review credits stay with your account and don't transfer. This is the last lifetime workspace you own, so after transferring you won't be able to redeem them until you own another workspace with an active lifetime code. The credits are not lost.
                  </p>
                </div>
              )}

              <div style={warningBoxStyle}>
                <AlertTriangle size={20} strokeWidth={1.5} color="#92400e" style={{ flexShrink: 0, marginTop: '2px' }} />
                <div>
                  <p style={{ fontSize: '0.875rem', fontWeight: 600, color: '#92400e', margin: '0 0 8px 0' }}>
                    Please review what happens during transfer:
                  </p>
                  <div style={{ fontSize: '0.8125rem', color: '#78350f', lineHeight: 1.7 }}>
                    <p style={{ margin: '0 0 8px 0' }}>
                      <strong>Transfers with workspace:</strong><br />
                      Infrastructure{isLifetime ? ', lifetime codes & limits' : ''}, custom domains
                    </p>
                    <p style={{ margin: '0' }}>
                      <strong>Stays with you:</strong><br />
                      Billing profile, payment methods{hasActiveSubscription && !isLifetime ? ' (subscription cancelled)' : ''}, invoice history, paid addons (deactivated)
                    </p>
                  </div>
                </div>
              </div>

              <p style={{ fontSize: '0.8125rem', color: 'var(--v2-text-tertiary)', margin: 0, lineHeight: 1.6 }}>
                The recipient has <strong>48 hours</strong> to accept. You can cancel anytime before they accept.
              </p>
            </>
          )}

          {/* Step 3: Success */}
          {step === 'success' && (
            <>
              <div style={successBoxStyle}>
                <Check size={20} strokeWidth={2} color="var(--v2-success-dark)" style={{ flexShrink: 0, marginTop: '2px' }} />
                <div>
                  <p style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--v2-success-dark)', margin: '0 0 4px 0' }}>
                    Transfer request sent!
                  </p>
                  <p style={{ fontSize: '0.8125rem', color: 'var(--v2-success-dark)', margin: 0 }}>
                    An email has been sent to <strong>{email}</strong> with instructions to accept.
                  </p>
                </div>
              </div>

              <div style={infoBoxStyle}>
                <p style={{ fontSize: '0.8125rem', color: 'var(--v2-text-secondary)', margin: 0, lineHeight: 1.6 }}>
                  The workspace will show as <strong>"Transfer Pending"</strong> until the recipient accepts or the request expires in 48 hours.
                  You can cancel the transfer anytime from the workspace menu.
                </p>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div style={footerStyle}>
          {step === 'email' && (
            <>
              <button
                onClick={handleClose}
                onMouseEnter={() => setCancelHovered(true)}
                onMouseLeave={() => setCancelHovered(false)}
                style={getSecondaryBtnStyle(cancelHovered)}
              >
                Cancel
              </button>
              <button
                onClick={() => setStep('confirm')}
                disabled={!isValidEmail}
                onMouseEnter={() => setNextHovered(true)}
                onMouseLeave={() => setNextHovered(false)}
                style={getPrimaryBtnStyle(nextHovered, !isValidEmail)}
              >
                Continue
              </button>
            </>
          )}

          {step === 'confirm' && (
            <>
              <button
                onClick={() => setStep('email')}
                onMouseEnter={() => setBackHovered(true)}
                onMouseLeave={() => setBackHovered(false)}
                style={getSecondaryBtnStyle(backHovered)}
              >
                Back
              </button>
              <button
                onClick={handleInitiateTransfer}
                disabled={submitting}
                onMouseEnter={() => setConfirmHovered(true)}
                onMouseLeave={() => setConfirmHovered(false)}
                style={getPrimaryBtnStyle(confirmHovered, submitting)}
              >
                {submitting && <Loader2 size={16} strokeWidth={2} style={{ animation: 'spin 1s linear infinite' }} />}
                {submitting ? 'Sending...' : 'Send Transfer Request'}
              </button>
            </>
          )}

          {step === 'success' && (
            <button
              onClick={handleClose}
              onMouseEnter={() => setDoneHovered(true)}
              onMouseLeave={() => setDoneHovered(false)}
              style={getPrimaryBtnStyle(doneHovered, false)}
            >
              Done
            </button>
          )}
        </div>
      </div>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default TransferWorkspaceModal;
